"use client";

import { Link, useParams, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { ChevronLeft, UserPlus } from "lucide-react";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CreateCustomerDto } from "@/types/models/customer";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store/store";
import {
  createCustomerRequest,
  resetFormState,
} from "@/store/slices/customer.slice";
import { StateCitySelector } from "@/components/common/StateCitySelector";

// Form validation schema
const customerSchema = z.object({
  first_name: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s]+$/, "First name can only contain letters and spaces"),
  last_name: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s]+$/, "Last name can only contain letters and spaces"),
  phone_number: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^[\+]?[1-9][\d]{0,15}$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(15, "Phone number must be less than 15 digits"),
  email: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: "Please enter a valid email address",
    }),
  address_line_one: z
    .string()
    .min(1, "Address Line 1 is required")
    .min(5, "Address must be at least 5 characters")
    .max(100, "Address must be less than 100 characters"),
  address_line_two: z
    .string()
    .max(100, "Address Line 2 must be less than 100 characters")
    .optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  pin_code: z
    .string()
    .min(1, "Pin code is required")
    .regex(/^[1-9][0-9]{5}$/, "Please enter a valid 6-digit pin code"),
  use_as_billing_address: z.boolean().optional(),
});

const CreateCustomer: React.FC = () => {
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const { formState } = useSelector((state: RootState) => state.customers);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<CreateCustomerDto>({
    resolver: zodResolver(customerSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Watch state and city values
  const selectedState = watch("state");
  const selectedCity = watch("city");

  // Handle form success
  useEffect(() => {
    if (formState.success) {
      toast.success("Customer created successfully");
      reset();
      dispatch(resetFormState());
      navigate(`/${storeType}/${storeId}/customers`);
    }
  }, [formState.success, reset, dispatch, navigate, storeType, storeId]);

  // Handle form error
  useEffect(() => {
    if (formState.error) {
      toast.error(formState.error.message || "Failed to create customer");
    }
  }, [formState.error]);

  const onSubmit: SubmitHandler<CreateCustomerDto> = (data) => {
    data.phone_number= "91"+data.phone_number;
    dispatch(createCustomerRequest(data));
  };

  const _handleSaveDraft = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleCreateCustomer = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const breadcrumbItems = [
    {
      label: "Customers",
      href: `/${storeType}/${storeId}/customers`,
    },
    {
      label: "Add Customer",
      isActive: true,
    },
  ];

  return (
    <div className="p-2 mb-6 relative">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} showBackButton={false} />
        </div>

        {/* Top menu */}
        <div className="flex items-center justify-between w-full mb-4">
          <div className="flex items-center justify-between gap-0">
            <Link to={`/${storeType}/${storeId}/customers`}>
              <button
                type="button"
                className="flex items-center px-3 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/80 rounded-r-none box-border h-10 cursor-pointer"
              >
                <ChevronLeft size={18} className="mr-1" /> Back
              </button>
            </Link>
            <span className="px-3 py-2 bg-card text-foreground rounded-full rounded-l-none border border-border box-border h-10">
              Add customer
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ButtonPrimary
              onClick={handleCreateCustomer}
              className="rounded-full"
              disabled={formState.loading || !isValid || !isDirty}
            >
              <UserPlus size={16} className="mr-1" />
              {formState.loading ? "Creating..." : "Create Customer"}
            </ButtonPrimary>
          </div>
        </div>

        {/* Form Content */}
        <div className="space-y-6">
          {/* Personal Information */}
          <FormFieldWrapper title="Personal Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StandardFormInput
                label="First Name"
                placeholder="Enter first name"
                {...register("first_name")}
                error={errors.first_name?.message}
                required
              />
              <StandardFormInput
                label="Last Name"
                placeholder="Enter last name"
                {...register("last_name")}
                error={errors.last_name?.message}
                required
              />
              <StandardFormInput
                label="Phone Number"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter phone number"
                {...register("phone_number")}
                error={errors.phone_number?.message}
                required
              />
              <StandardFormInput
                label="Email"
                placeholder="Enter email (optional)"
                type="email"
                {...register("email")}
                error={errors.email?.message}
              />
            </div>
          </FormFieldWrapper>

          {/* Address Information */}
          <FormFieldWrapper title="Address Information">
            <div className="space-y-4">
              <StandardFormInput
                label="Address Line 1"
                placeholder="Enter address line 1"
                {...register("address_line_one")}
                error={errors.address_line_one?.message}
                required
              />
              <StandardFormInput
                label="Address Line 2"
                placeholder="Enter address line 2 (optional)"
                {...register("address_line_two")}
                error={errors.address_line_two?.message}
              />
              <div className="space-y-4">
                <StateCitySelector
                  selectedState={selectedState}
                  selectedCity={selectedCity}
                  onStateChange={(state) => setValue("state", state)}
                  onCityChange={(city) => setValue("city", city)}
                  stateError={errors.state?.message}
                  cityError={errors.city?.message}
                  stateHasError={!!errors.state}
                  cityHasError={!!errors.city}
                  required={true}
                />
                <StandardFormInput
                  label="Pin Code"
                  placeholder="Enter pin code"
                  {...register("pin_code")}
                  error={errors.pin_code?.message}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="use_as_billing_address"
                  {...register("use_as_billing_address")}
                  className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label
                  htmlFor="use_as_billing_address"
                  className="text-sm text-foreground"
                >
                  Use as billing address
                </label>
              </div>
            </div>
          </FormFieldWrapper>
        </div>

        {/* Form Error Display */}
        {formState.error && (
          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-destructive rounded-full"></div>
              <p className="text-sm font-medium text-destructive">
                Failed to create customer
              </p>
            </div>
            <p className="text-sm text-destructive/80 mt-1">
              {formState.error.message}
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateCustomer;
